/**
 * ServiceError Middleware
 *
 * Intercepts service error messages. Handles app wide concerns related to service errors
 *
 * @module middleware/ServiceError
 */
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import { push } from 'react-router-redux';

import { bufferApiCalls, listenerReauth } from '../actions/Application';
import { openServiceErrorToast } from '../actions/Modal';
import { ActionTypes, ApiEndpoints, ApiToken, AUTH_ACTIONS } from '../constants/ApiActions';
import { AppActions } from '../constants/App';
import { getInDomain } from '../domains/externalUtils';
import createActionMapping from '../lib/ActionRouter';
import { isLoginStyleAction } from '../lib/ApiUtils';
import {
  hasServiceErrorCountExceededMax,
  isApplicationInitialized,
  isServiceOk,
} from '../lib/ApplicationUtil';
import { ERROR_TYPE_API } from '../lib/DatadogClient';
import ApiError, { ApiErrorTypes } from '../lib/error/ApiError';
import * as ListenerUtils from '../lib/ListenerUtils';
import {
  getCurrentPathnameFromState,
  getForgotPasswordPath,
  getLoginPath,
  getResetPasswordPath,
  getServiceErrorPath,
  isLoginPath,
  isLogoutPath,
  isRegisterPath,
  isRootPath,
} from '../lib/RouteUtils';
import { getPersistor } from '../store/StoreAccessor';
import Logger from '../tools/Logger';
import * as Utils from '../utils';

const log = Logger.child({
  component: 'middleware/ServiceError',
  errorType: ERROR_TYPE_API,
});

/* Several HTTP 400 error messages indicate invalid auth.
 * atm they do not carry an identifiable code, need to map to error message */
const INVALID_AUTH_MESSAGES = [
  'No auth token found',
  'The request could not be validated',
  'Invalid username and/or password',
];

/**
 * Is this an action the API will handle
 *
 * @param {object} action - redux action
 * @returns {boolean} true/false
 */
function isApiAction(action) {
  return !isUndefined(action[ApiToken]);
}

function isCoordinatedServiceError(action) {
  if (!isUndefined(action)) {
    return action.type === ActionTypes.SEND_SERVICE_ERROR_TO_MIDDLEWARE;
  }
  return false;
}

/**
 * Is this an auth API action
 *
 * @param {object} action - redux action
 * @returns {boolean} true/false
 */
function isAuthApiCall(action) {
  return (
    isApiAction(action) &&
    action[ApiToken].types &&
    AUTH_ACTIONS.indexOf(action[ApiToken].types.start) !== -1
  );
}

/**
 * Create closure over a buffered API action
 *  that can be resolved once auth resolves
 *
 * @param {object} action - an API action to hold on to
 * @param {object[]} bufferedActions  - queue that holds API actions being buffered
 * @returns {Promise} - a promise that will get resolved once auth succeeds
 */
function bufferAction(action, bufferedActions) {
  return new Promise((resolve, reject) => {
    bufferedActions.push({
      action,
      resolve,
      reject,
    });
  });
}

/**
 * Checks whether auth state & service are ok
 *
 * @param {Function} getState - get redux state
 * @returns {boolean} true/false
 */
function isServiceAndAuthResolved(getState) {
  const state = getState();
  return isServiceOk(state) && ListenerUtils.isAuthenticated(state);
}

/**
 * Intercept a login success to release any buffered API calls
 *
 * @param {object[]} bufferedActions  - queue that holds API actions being held
 * @param {Function} dispatch - store dispatch
 * @param {Function} getState - get redux state
 */
const releaseBufferedActions = (bufferedActions, { dispatch, getState }) => {
  if (
    bufferedActions.length === 0 &&
    !getInDomain('application.utils.isBufferingApiCalls', false)(getState())
  ) {
    return;
  }
  dispatch(bufferApiCalls(false));
  while (bufferedActions.length > 0) {
    const activity = bufferedActions.shift();
    dispatch(activity.action).then(activity.resolve).catch(activity.reject);
  }
};

/**
 * Handle reported server errors.
 * Show a toast with the error. If error exceeds a threshold
 *  redirect to Service Error page
 *
 * @param {Function} dispatch - store dispatch
 * @param {Function} getState - get redux state
 * @param {object} router - react-router
 * @param {object} error - service error obj
 * @returns {object} - return handled false so request is not trapped and can be handled down the line
 */
function onHandleServiceError(dispatch, getState, router, error) {
  if (hasServiceErrorCountExceededMax(getState())) {
    dispatch(push(getServiceErrorPath()));
  } else {
    const message = error && error.errorMessage;
    log.error(message || 'No Message');
    dispatch(openServiceErrorToast());
  }
  return {
    handled: false,
  };
}

/**
 * Handle auth failure that requires explicit login
 *
 * @param {object} dispatch - dispatch
 * @param {boolean} shouldReroute - whether it should route to the Login page
 * @returns {object} - return handled false so request is not trapped and can be handled down the line
 */
function onHandleAuthError(dispatch, shouldReroute) {
  if (shouldReroute) {
    dispatch(push(getLoginPath()));
  }
  return {
    handled: false,
  };
}

/**
 * Auth expired, attempt re-auth call & start buffering API actions
 *  that come through
 *
 * @param {Function} getState - function to get redux state
 * @param {Function} dispatch - store dispatch
 * @param {object[]} bufferedActions  - queue that holds API actions being held
 * @param {object} action - the action that triggered auth expired
 * @returns {object} - handled true so error is not delegated to the next middleware,
 *  and a promise that will get resolved internally once auth error is resolved
 */
function onHandleAuthExpired(getState, dispatch, bufferedActions, action) {
  let replay;
  const isLoggingIn = ListenerUtils.isLoggingIn(getState());
  if (isLoginStyleAction(action)) {
    // We shouldn't buffer failed login action since
    // it can lead to an infinite loop if they keep failing
    replay = Promise.reject();
    // If login has failed and the user is not logging in we want to redirect to the login page.
    if (!isLoggingIn) {
      dispatch(push(getLoginPath()));
    }
  } else {
    replay = bufferAction(action, bufferedActions);
  }
  dispatch(bufferApiCalls(true));
  if (!isLoggingIn) {
    const request = action[ApiToken];
    const endpoint = request.endpoint;
    const continueSource =
      [ApiEndpoints.PLAYBACK_PEEK, ApiEndpoints.ACTION_REPEAT].includes(endpoint) ||
      (endpoint === ApiEndpoints.EVENT_STARTED && (request.content.elapsedTime || 0) > 5);
    dispatch(listenerReauth(continueSource));
    try {
      const persistor = getPersistor();
      persistor.purge(['source']);
    } catch (e) {
      log.warn(e, 'unable to purge source during onHandleAuthExpired');
    }
  }
  return {
    handled: true,
    replay,
  };
}

/**
 * Evaluate service error calls.
 * On 5xx errors, show a message.
 * On 400 error, force re-login since its not a recoverable auth
 * On 401, attempt to silently re-auth
 *
 * @param {object} router -react-router
 * @param {object[]} bufferedActions  - queue that holds API actions being held
 * @returns {Promise} - promise
 */
const onServiceError =
  (router, bufferedActions) =>
  (action, { dispatch, getState }, error) => {
    if (isUndefined(error)) {
      return {
        handled: false,
      };
    }

    if (error.status >= 500 && error.status <= 599) {
      return onHandleServiceError(dispatch, getState, router, error);
    }

    if (error.status === 400 && INVALID_AUTH_MESSAGES.indexOf(error.errorMessage) !== -1) {
      const isLoggedIn = Utils.Listener.isLoggedIn(getState());
      const buffering = getInDomain('application.utils.isBufferingApiCalls', false)(getState());
      // buffering should be set to true after receiving a 401 response
      const isPendingPurchase = getInDomain(
        'payments.selectors.getIsPendingPurchase',
        false,
      )(getState());
      const currentPathname = getCurrentPathnameFromState(getState());
      const isOnLoginPage = isLoginPath(currentPathname);
      const isOnRegisterPage = isRegisterPath(currentPathname);
      const isOnLandingPage = isRootPath(currentPathname);
      const isOnLogoutPage = isLogoutPath(currentPathname);
      const isOnResetPasswordPage = getResetPasswordPath() === currentPathname;
      const isOnForgotPasswordPage = getForgotPasswordPath() === currentPathname;
      // Don't go to login page if still logged in or just upgraded, thus invalidating the auth token
      // The router would just route you back
      if (!isLoggedIn && !buffering && !isPendingPurchase) {
        // Don't go to the login page if you're already on a valid logged-out page, but still call the handler for its return value
        const shouldReroute = !(
          isOnLoginPage ||
          isOnLogoutPage ||
          isOnRegisterPage ||
          isOnLandingPage ||
          isOnResetPasswordPage ||
          isOnForgotPasswordPage
        );
        log.error(
          { apiError: error },
          `Auth service error for ${get(error, 'action.endpoint')}: ${error.status} - ${
            error.errorMessage
          }`,
        );
        return onHandleAuthError(dispatch, shouldReroute);
      }

      if (!isServiceOk(getState())) {
        return {
          handled: true,
          replay: bufferAction(action, bufferedActions),
        };
      }
    }

    if (error.status === 401) {
      return onHandleAuthExpired(getState, dispatch, bufferedActions, action);
    }

    if (error.status === 429 && error.errorType === ApiErrorTypes.STREAM_VIOLATION) {
      return { handled: false };
    }

    log.error(
      error,
      `Unhandled service error for ${get(error, 'action.endpoint')}: ${error.status} - ${
        error.errorMessage
      }`,
    );

    return {
      handled: false,
    };
  };

/**
 * On application initialized, monitor when app has left error state
 *  so buffered API calls can be released
 *
 * @param {object} localState - holds the buffer of actions and a flag indicating they are being released
 * @param {object} action - current action
 * @param {Function} getState - current application state
 * @returns {Promise} resolves on complete
 */
const onApplicationInitialize =
  localState =>
  (action, { getState }) => {
    return new Promise(resolve => {
      if (!isApplicationInitialized(getState())) {
        const store = action.payload.store;
        store.subscribe(() => {
          if (localState.isReleasing) {
            return;
          }
          localState.isReleasing = true;
          if (isServiceAndAuthResolved(getState)) {
            releaseBufferedActions(localState.bufferedActions, store);
          }
          localState.isReleasing = false;
        });
      }
      return resolve();
    });
  };

/**
 * Map incoming actions to their workflow controllers
 *
 * @param {object} router - react-router
 * @param {object} localState - holds the buffer of actions and a flag indicating they are being released
 * @returns {Function} maps action to handler
 */
export const route = (router, localState) => {
  return createActionMapping({
    [AppActions.APPLICATION_INITIALIZE]: onApplicationInitialize(localState),
  });
};

function maybeCallApi(api, action, coordinatedError) {
  return coordinatedError ? Promise.reject(coordinatedError) : api(action);
}

/**
 * Pass incoming actions to ActionRouter
 *
 * @param  {object} router - react router
 * @param  {Function} apiMiddleware - API middleware, get decorated by ServiceError
 * @param  {object} store - redux
 * @param  {object} next - dispatch function of next middleware in the chain
 * @param  {object} action - the current action
 * @returns {Promise} resolution of the workflow
 */
export default (router, apiMiddleware) => store => {
  /* buffer in which to keep actions & flag to prevent re-entry. */
  const localState = {
    bufferedActions: [],
    isReleasing: false,
  };

  return next => {
    const api = apiMiddleware(store)(next);
    const serviceErrorHandler = onServiceError(router, localState.bufferedActions);

    return originalAction => {
      // We keep an api service that can be called outside of the middleware context
      // and these checks for coordinatedErrors ensure that errors that occur in that
      // context can be processed in tandem with errors with middleware based API actions
      const isCoordinatedServiceCall = isCoordinatedServiceError(originalAction);
      let coordinatedError;
      let action = originalAction;

      if (isCoordinatedServiceCall) {
        action = originalAction.payload.action;
        coordinatedError = originalAction.payload.error;
      }

      const isApiCall = isApiAction(action);
      if (isApiCall || isCoordinatedServiceCall) {
        const buffering = getInDomain(
          'application.utils.isBufferingApiCalls',
          false,
        )(store.getState());
        if (buffering && !isAuthApiCall(action)) {
          /* hold onto non-auth API call. will be re-dispatched once auth success */
          return bufferAction(action, localState.bufferedActions);
        }
        /* delegate to decorated API middleware. Listen for error and handle those we care about locally */
        return new Promise((resolve, reject) => {
          maybeCallApi(api, action, coordinatedError)
            /* API completed, nothing to do, resolve */
            .then(resolve)
            /* API error, handle within our promise */
            .catch(error => {
              if (error instanceof ApiError) {
                const { handled, replay } = serviceErrorHandler(action, store, error);
                if (handled) {
                  /* we can do something about this error. Once auth succeeds it will replay */
                  replay
                    /* replay success. resolve wrapper promise so original dispatcher carries on */
                    .then(resolve)
                    /* another error. rethrow so original dispatcher knows. */
                    .catch(reject);
                } else {
                  /* not an error we can do anything about. rethrow so original dispatcher knows */
                  reject(error);
                }
              } else {
                reject(error);
              }
            });
        });
      }
      return route(router, localState)(action, store, next);
    };
  };
};
